import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, finalize, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs'
import { HomeState } from '../data-access/state/home.state'

import { ChantierDto } from '../data-access/http/dto/chantier.dto'
import { ChantierHttp } from '../data-access/http/chantier.http'
import { Chantier } from '../../shared/models/chantier'
import { ChantierMapper } from './mapper/chantier.mapper'
import { SousTraitantsDto } from '../data-access/http/dto/sous-traitants.dto'
import { SousTraitantsMapper } from './mapper/sous-traitants.mapper'
import { SousTraitantsHttp } from '../data-access/http/sous-traitants.http'
import { PageResponse } from '../../shared/data-access/http/dto/page-dto'
import { SousTraitant } from '../../shared/models/sous-traitant'
import { InsertionChantierHttpService } from '../data-access/http/insertion-chantier.http'
import { HeurePointeeChantierDto } from '../data-access/http/dto/heure-pointee-chantier.dto'
import { HeurePointeeHttp } from '../data-access/http/heure-pointee.http'
import { InsertionChantierDto } from '../data-access/http/dto/insertion-chantier.dto'
import { InsertionChantierType } from '../../shared/models/enum/insertion-chantier-type.enum'
import { Salarie } from '../../shared/models/salarie'
import { SalarieDto } from '../data-access/http/dto/salarie.dto'
import { SalarieHttp } from '../data-access/http/salarie.http'
import { SalarieMapper } from './mapper/salarie.mapper'
import { HeurePointeeChantier } from '../../shared/models/heure-pointee-chantier'
import { HeurePointeeInsertion } from '../../shared/models/heure-pointee-insertion'
import { HeurePointeeChantierMapper } from './mapper/heure-pointee.mapper'
import { CreateInsertionChantierDto } from '../data-access/http/dto/create-insertion-chantier.dto'
import { UserState } from '../../shared/data-access/state/user.state'
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastrService } from 'ngx-toastr'
import {
  PieceJointe,
  PieceJointeModalComponent,
} from '../ui/heures-insertion-dashboard/piece-jointe-modal/piece-jointe-modal.component'
import { PieceJointeHttp } from '../../shared/data-access/http/piece-jointe.http'
import { InsertionChantierPieceJointeHttp } from '../data-access/http/insertion-chantier-piece-jointe.http'
import { ModeIntervenant } from '../../shared/models/enum/mode-intervenant.enum'
import { EvenementDomainService } from '../../shared/domain/evenement.domain'
import { EvenementDto } from '../../shared/data-access/http/dto/evenement-dto'

@Injectable({ providedIn: 'root' })
export class HomeDomain {
  private readonly chantierHttp = inject(ChantierHttp)
  private readonly sousTraitantsHttp = inject(SousTraitantsHttp)
  private readonly salarieHttp = inject(SalarieHttp)
  private readonly insertionChantierHttpService = inject(InsertionChantierHttpService)
  private readonly heurePointeeHttp = inject(HeurePointeeHttp)
  private readonly pieceJointeHttp = inject(PieceJointeHttp)
  private readonly homeState = inject(HomeState)
  private readonly userState = inject(UserState)
  private readonly modalService = inject(NgbModal)
  private readonly toastr = inject(ToastrService)
  private readonly insertionChantierPieceJointeHttp = inject(InsertionChantierPieceJointeHttp)
  private readonly evenementDomainService = inject(EvenementDomainService)
  // Selectors
  selectedChantier$: Observable<Chantier> = this.homeState.store
    .select(HomeState.getSelectedChantier)
    .pipe(map(ChantierMapper.dtoToModel))
  isModeIntervenantCompagnon$: Observable<boolean> = this.selectedChantier$.pipe(
    map((chantier) => chantier && chantier.modeIntervenant === ModeIntervenant.COMPAGNON)
  )
  searchedSousTraitants$: Observable<SousTraitant[]> = this.homeState.store
    .select(HomeState.getSearchedSousTraitants)
    .pipe(map(SousTraitantsMapper.dtosToModels))
  searchedSalaries$: Observable<Salarie[]> = this.homeState.store
    .select(HomeState.getSearchedSalaries)
    .pipe(map((salaries) => salaries.map(SalarieMapper.dtoToModel)))
  heuresPointeesChantier$: Observable<HeurePointeeChantier> = this.homeState.store
    .select(HomeState.getheurePointeeChantier)
    .pipe(map(HeurePointeeChantierMapper.dtoToModel))
  engagementLg$: Observable<number> = this.homeState.store.select(HomeState.getEngagementLg)
  heuresPointeesSousTraitants$: Observable<HeurePointeeInsertion[]> = this.generateHeurePointeeObservableByType([
    InsertionChantierType.SOUS_TRAITANT,
  ])
  heuresPointeesAutresCas$: Observable<HeurePointeeInsertion[]> = this.generateHeurePointeeObservableByType([
    InsertionChantierType.AUTRE_CAS,
  ])
  heuresPointeesSalaries$: Observable<HeurePointeeInsertion[]> = this.generateHeurePointeeObservableByType([
    InsertionChantierType.AUTRE_SALARIE_LG,
  ])
  heuresPointeesCompagnon$: Observable<HeurePointeeInsertion[]> = this.generateHeurePointeeObservableByType([
    InsertionChantierType.COMPAGNON_LG,
    InsertionChantierType.COMPAGNON_ETT,
  ])

  heuresPointeesCompagnonsSousTraitants$: Observable<
    Array<{
      entreprise: HeurePointeeInsertion
      compagnons: HeurePointeeInsertion[]
    }>
  > = combineLatest([
    this.homeState.store.select(HomeState.getInsertionsChantier),
    this.homeState.store.select(HomeState.getheurePointeeChantier),
  ]).pipe(
    map(([insertionsChantier, heurePointeeChantier]) => {
      if (!insertionsChantier || !heurePointeeChantier) {
        return []
      }

      const heuresMap = new Map(heurePointeeChantier.heures.map((h) => [h.id, h]))

      const entreprises = insertionsChantier
        .filter((i) => i.type === InsertionChantierType.SOUS_TRAITANT)
        .sort((a, b) => a.id - b.id)

      const compagnons = insertionsChantier
        .filter((i) => i.type === InsertionChantierType.COMPAGNON_SOUS_TRAITANT)
        .sort((a, b) => a.id - b.id)

      return entreprises.map((entreprise) => {
        const heurePointeeDto = heuresMap.get(entreprise.id)

        const entrepriseData: HeurePointeeInsertion = {
          ...entreprise,
          realiseTaux: heurePointeeDto?.realiseTaux || -1,
          nbHeures: heurePointeeDto?.nbHeures || 0,
          nbHeuresEcole: heurePointeeDto?.nbHeuresEcole || 0,
          nbHeuresFormation: heurePointeeDto?.nbHeuresFormation || 0,
          nbHeuresChantier: heurePointeeDto?.nbHeuresChantier || 0,
          nbHeuresTotales: heurePointeeDto?.nbHeuresTotales || 0,
          projection: heurePointeeDto?.projection || '',
          engagement: heurePointeeDto?.engagement || entreprise.engagement || 0,
          commentaire: heurePointeeDto?.commentaire || '',
          periode: heurePointeeChantier.periode || '',
          isCompagnonETT: false,
          nbPieceJointe: heurePointeeDto?.nbPieceJointe || 0,
        }

        const compagnonsEntreprise = compagnons
          .filter((c) => c.parentId === entreprise.id)
          .map((compagnon) => {
            const heurePointeeCompagnonDto = heuresMap.get(compagnon.id)

            return {
              ...compagnon,
              realiseTaux: heurePointeeCompagnonDto?.realiseTaux || -1,
              nbHeures: heurePointeeCompagnonDto?.nbHeures || 0,
              nbHeuresTotales: heurePointeeCompagnonDto?.nbHeuresTotales || 0,
              projection: heurePointeeCompagnonDto?.projection || '',
              engagement: heurePointeeCompagnonDto?.engagement || 0,
              commentaire: heurePointeeCompagnonDto?.commentaire || '',
              periode: heurePointeeChantier.periode || '',
              isCompagnonETT: false,
              nbPieceJointe: heurePointeeCompagnonDto?.nbPieceJointe || 0,
            }
          })

        return {
          entreprise: entrepriseData,
          compagnons: compagnonsEntreprise,
        }
      })
    })
  )

  // Actions
  updateFacilitateur(facilitateur: string): Observable<ChantierDto> {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)
    return this.chantierHttp
      .patchChantier(selectedChantier.code, {
        facilitateur,
      })
      .pipe(
        tap((updatedChantier) => {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: updatedChantier,
          }))
        })
      )
  }

  updateTotalChantier(engagementTotal: number): Observable<ChantierDto> {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)

    const engagementAvant = selectedChantier.engagementTotal
    const engagementApres = engagementTotal

    return this.chantierHttp
      .patchChantier(selectedChantier.code, {
        engagementTotal,
      })
      .pipe(
        tap((updatedChantier) => {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: updatedChantier,
          }))

          this.evenementDomainService
            .createModificationEngagementEvenement(
              selectedChantier.code,
              engagementAvant.toString(),
              engagementApres.toString()
            )
            .subscribe()
        })
      )
  }

  updateEngagementLg(engagementLg: number): Observable<ChantierDto> {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)
    return this.chantierHttp
      .patchChantier(selectedChantier.code, {
        engagementLg,
      })
      .pipe(
        tap((updatedChantier) => {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: updatedChantier,
          }))
        })
      )
  }

  loadChantier(codeChantier: string): Observable<ChantierDto> {
    return this.chantierHttp.getChantierByCode(codeChantier).pipe(
      tap((chantier) => {
        if (chantier) {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: chantier,
          }))
        }
      })
    )
  }

  findSousTraitantsByKeyword(keyword: string): Observable<PageResponse<SousTraitantsDto>> {
    const selectedChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    return this.sousTraitantsHttp.findSousTraitants(keyword || '', selectedChantierCode, 0, 10).pipe(
      tap((sousTraitantsPage) => {
        this.homeState.store.update((state) => ({
          ...state,
          searchedSousTraitants: sousTraitantsPage.content,
        }))
      })
    )
  }

  findSalariesByKeyword(keyword: string): Observable<PageResponse<SalarieDto>> {
    const selectedChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    return this.salarieHttp.findSalariesByKeyword(keyword || '', selectedChantierCode).pipe(
      tap((salariesPage) => {
        this.homeState.store.update((state) => ({
          ...state,
          searchedSalaries: salariesPage.content,
        }))
      })
    )
  }

  loadHeurePointeeByChantierCodeForSelectedPeriod(codeChantier: string): Observable<HeurePointeeChantierDto> {
    const period = this.homeState.store.selectSnapshot(HomeState.getPeriod)
    return this.heurePointeeHttp.getHeurePointeeByChantierCodeAndPeriode(codeChantier, period).pipe(
      tap((heurePointee: HeurePointeeChantierDto) => {
        this.homeState.store.update((state) => ({
          ...state,
          heurePointeeChantier: heurePointee,
        }))
      })
    )
  }

  refreshHeurePointeeForSelectedPeriod(): Observable<HeurePointeeChantierDto> {
    const selectedPeriod = this.homeState.store.selectSnapshot(HomeState.getPeriod)
    const currentChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    if (!currentChantierCode) {
      console.error("Pas de selectedChantier dans le state, impossible de rafraichir les données d'heures pointées")
      return of(null)
    }
    return this.heurePointeeHttp.getHeurePointeeByChantierCodeAndPeriode(currentChantierCode, selectedPeriod).pipe(
      tap((heurePointee: HeurePointeeChantierDto) => {
        this.homeState.store.update((state) => ({
          ...state,
          heurePointeeChantier: heurePointee,
        }))
      })
    )
  }

  addSousTraitantToSelectedChantier(sousTraitantCode: string): Observable<InsertionChantierDto> {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)
    const dto: CreateInsertionChantierDto = {
      key: sousTraitantCode,
      type: InsertionChantierType.SOUS_TRAITANT,
      nom: '',
    }
    return this.insertionChantierHttpService.createInsertionChantier(selectedChantier.code, dto).pipe(
      tap(() => {
        this.homeState.store.update((state) => ({
          ...state,
          searchedSousTraitants: [],
        }))
      }),
      switchMap((newSousTraitant: InsertionChantierDto): Observable<InsertionChantierDto> => {
        return this.evenementDomainService
          .createAjoutEntrepriseEvenement(selectedChantier.code, newSousTraitant)
          .pipe(map((): InsertionChantierDto => newSousTraitant))
      })
    )
  }

  updateEngagement(idInsertionChantier: number, engagement: number): Observable<InsertionChantierDto> {
    return this.insertionChantierHttpService.patchInsertionChantier(idInsertionChantier, { engagement: engagement })
  }

  updateRealisesMois(id: number, realisesMoisValue: number): Observable<HeurePointeeChantierDto> {
    const periode = this.homeState.store.selectSnapshot(HomeState.getPeriod)
    return this.heurePointeeHttp.patchHeurePointee(id, periode, {
      nbHeures: realisesMoisValue,
    })
  }

  updateCommentairesMois(id: number, commentaire: string): Observable<HeurePointeeChantierDto> {
    const periode = this.homeState.store.selectSnapshot(HomeState.getPeriod)

    return this.heurePointeeHttp.patchHeurePointee(id, periode, {
      commentaire,
    })
  }

  updateModeIntervenant(isModeCompagnon: boolean): void {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)
    const modeIntervenant = isModeCompagnon ? ModeIntervenant.COMPAGNON : ModeIntervenant.ENTREPRISE

    this.chantierHttp
      .patchChantier(selectedChantier.code, {
        modeIntervenant,
      })
      .pipe(
        tap((updatedChantier) => {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: updatedChantier,
            modeSousTraitants: isModeCompagnon,
          }))
        }),
        switchMap(() =>
          combineLatest([this.refreshAllInsertionsChantier(), this.refreshHeurePointeeForSelectedPeriod()])
        )
      )
      .subscribe()
  }

  loadAllInsertionsChantier(codeChantier: string): Observable<InsertionChantierDto[]> {
    return this.insertionChantierHttpService.getAllInsertionsChantierByCodeChantier(codeChantier).pipe(
      tap((insertionsChantier) =>
        this.homeState.store.update((state) => ({
          ...state,
          insertionsChantier,
        }))
      )
    )
  }

  refreshAllInsertionsChantier(): Observable<InsertionChantierDto[]> {
    const currentChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    if (!currentChantierCode) {
      console.error("Pas de selectedChantier dans le state, impossible de rafraichir les données d'insertions chantier")
      return of([])
    }
    return this.insertionChantierHttpService.getAllInsertionsChantierByCodeChantier(currentChantierCode).pipe(
      tap((insertionsChantier) =>
        this.homeState.store.update((state) => ({
          ...state,
          insertionsChantier,
        }))
      )
    )
  }

  deleteInsertionChantier(id: number): Observable<void> {
    const insertionChantier = this.homeState.store.selectSnapshot(HomeState.getInsertionChantierById(id))
    const heurePointeeChantier = this.homeState.store.selectSnapshot(HomeState.getheurePointeeChantier)
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)

    if (!insertionChantier) {
      return of(void 0)
    }

    return this.showDeleteModal(insertionChantier, heurePointeeChantier, id).pipe(
      switchMap((result: string): Observable<void> => {
        if (result === 'confirm') {
          return this.insertionChantierHttpService.deleteInsertionChantier(id).pipe(
            switchMap((): Observable<EvenementDto> => {
              return this.evenementDomainService.createSuppressionEntrepriseEvenement(
                selectedChantier.code,
                insertionChantier
              )
            }),
            map((): void => void 0)
          )
        }
        return of(void 0)
      })
    )
  }

  addSousTraitantByNameToSelectedChantier(sousTraitantName: string): Observable<InsertionChantierDto> {
    const selectedChantier = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)

    return this.insertionChantierHttpService
      .createInsertionChantier(selectedChantier.code, {
        key: '',
        nom: sousTraitantName,
        type: InsertionChantierType.SOUS_TRAITANT,
      })
      .pipe(
        tap(() => {
          this.homeState.store.update((state) => ({
            ...state,
            searchedSousTraitants: [],
          }))
        }),
        switchMap((newSousTraitant: InsertionChantierDto): Observable<InsertionChantierDto> => {
          return this.evenementDomainService
            .createAjoutEntrepriseEvenement(selectedChantier.code, newSousTraitant)
            .pipe(map((): InsertionChantierDto => newSousTraitant))
        })
      )
  }

  addSousTraitantCompagnonByNameToSelectedChantier(
    parentId: number,
    sousTraitantName: string
  ): Observable<InsertionChantierDto> {
    const selectedChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    return this.insertionChantierHttpService
      .createInsertionChantier(selectedChantierCode, {
        key: '',
        nom: sousTraitantName,
        type: InsertionChantierType.COMPAGNON_SOUS_TRAITANT,
        parentId: parentId,
      })
      .pipe(
        tap(() => {
          this.homeState.store.update((state) => ({
            ...state,
            searchedSousTraitants: [],
          }))
        })
      )
  }
  addAutreCasLGByName(autreCasLGName: string): Observable<InsertionChantierDto> {
    const selectedChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    return this.insertionChantierHttpService.createInsertionChantier(selectedChantierCode, {
      key: '',
      nom: autreCasLGName,
      type: InsertionChantierType.AUTRE_CAS,
    })
  }

  addSalarie(salarieMatricule: string): Observable<InsertionChantierDto> {
    const selectedChantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code
    return this.insertionChantierHttpService
      .createInsertionChantier(selectedChantierCode, {
        key: salarieMatricule,
        nom: '',
        type: InsertionChantierType.AUTRE_SALARIE_LG,
      })
      .pipe(
        tap(() => {
          this.homeState.store.update((state) => ({
            ...state,
            searchedSalaries: [],
          }))
        })
      )
  }

  updatePeriod(period: string): void {
    this.homeState.store.update((state) => ({ ...state, period }))
    this.refreshHeurePointeeForSelectedPeriod().subscribe()
  }

  exportAsGoogleSheet(): Observable<void> {
    const userEmail = this.userState.store.selectSnapshot(UserState.getConnectedUser)?.email
    const chantierCode = this.homeState.store.selectSnapshot(HomeState.getSelectedChantier)?.code

    this.evenementDomainService.createExportSheetEvenement(chantierCode).subscribe()

    return this.heurePointeeHttp
      .exportAsGoogleSheet(chantierCode)
      .pipe(tap(() => this.toastr.success("Vous recevrez l'export par email à l'adresse " + userEmail)))
  }

  updateInsertionChantierName(idInsertionChantier: number, newName: string): Observable<InsertionChantierDto> {
    return this.insertionChantierHttpService.patchInsertionChantier(idInsertionChantier, { nom: newName })
  }

  openPieceJointeModal(idInsertionChantier: number): Observable<unknown> {
    const period = this.homeState.store.selectSnapshot(HomeState.getPeriod)
    const loading$ = new BehaviorSubject<boolean>(false)

    const refreshTrigger$ = new Subject<void>()

    const pieceJointes$ = refreshTrigger$.pipe(
      startWith(null),
      switchMap(() => this.getPiecesJointesForInsertionChantier(idInsertionChantier))
    )

    const modalRef = this.modalService.open(PieceJointeModalComponent, {
      size: 'md',
      centered: true,
    })

    modalRef.componentInstance.pieceJointes = pieceJointes$
    modalRef.componentInstance.loading = loading$

    modalRef.componentInstance.uploadDocument.subscribe((file: File) => {
      loading$.next(true)
      this.pieceJointeHttp
        .uploadToAwsLambda(file)
        .pipe(
          switchMap((lambdaId) =>
            this.insertionChantierPieceJointeHttp.uploadPieceJointeInsertionChantier(idInsertionChantier, period, {
              filename: file.name,
              lambdaId,
            })
          ),
          tap(() => refreshTrigger$.next()),
          finalize(() => loading$.next(false))
        )
        .subscribe(() => this.toastr.success('Pièce jointe ajoutée !'))
    })

    modalRef.componentInstance.deleteDocument.subscribe((idPieceJointe: number) => {
      loading$.next(true)
      this.pieceJointeHttp
        .deletePieceJointe(idPieceJointe)
        .pipe(
          tap(() => refreshTrigger$.next()),
          finalize(() => loading$.next(false))
        )
        .subscribe(() => this.toastr.success('Pièce jointe supprimée !'))
    })

    modalRef.componentInstance.openDocument.subscribe((pj: PieceJointe) => {
      window.open(pj.url, '_blank')
    })

    return modalRef.closed.pipe(tap(() => refreshTrigger$.complete()))
  }

  private getPiecesJointesForInsertionChantier(idInsertionChantier: number): Observable<PieceJointe[]> {
    const period = this.homeState.store.selectSnapshot(HomeState.getPeriod)
    const heuresPointeesInsertionChantier = {
      ...this.homeState.store.selectSnapshot(HomeState.getHeurePointeeByInsertionId(idInsertionChantier)),
    }
    return this.insertionChantierPieceJointeHttp
      .getPieceJointeByInsertionChantierIdAndPeriode(idInsertionChantier, period)
      .pipe(
        tap((pieceJointes) => {
          heuresPointeesInsertionChantier.nbPieceJointe = pieceJointes.length
          this.homeState.store.update((state) => ({
            ...state,
            heurePointeeChantier: {
              ...state.heurePointeeChantier,
              heures: [
                ...state.heurePointeeChantier.heures.filter((heure) => heure.id !== idInsertionChantier),
                heuresPointeesInsertionChantier,
              ],
            },
          }))
        })
      )
  }

  private generateHeurePointeeObservableByType(
    insertionChantierTypes: InsertionChantierType[]
  ): Observable<HeurePointeeInsertion[]> {
    return combineLatest([
      this.homeState.store.select(HomeState.getInsertionsChantier),
      this.homeState.store.select(HomeState.getheurePointeeChantier),
    ]).pipe(
      map(([insertionsChantier, heurePointeeChantier]) => {
        if (!insertionsChantier || !heurePointeeChantier) return []

        return insertionsChantier
          .filter((insertion) => insertionChantierTypes.includes(insertion.type))
          .map((insertion) => {
            const heurePointeeDto = this.homeState.store.selectSnapshot(
              HomeState.getHeurePointeeByInsertionId(insertion.id)
            )
            const isCompagnonETT = insertion.type === InsertionChantierType.COMPAGNON_ETT

            if (!heurePointeeDto) {
              return {
                ...insertion,
                realiseTaux: -1,
                nbHeures: 0,
                nbHeuresEcole: 0,
                nbHeuresFormation: 0,
                nbHeuresTotales: 0,
                nbHeuresChantier: 0,
                projection: '',
                engagement: insertion.engagement || 0,
                commentaire: '',
                periode: '',
                isCompagnonETT,
                nbPieceJointe: 0,
              }
            }

            return {
              ...insertion,
              realiseTaux: heurePointeeDto.realiseTaux || 0,
              nbHeures: heurePointeeDto.nbHeures || 0,
              nbHeuresEcole: heurePointeeDto?.nbHeuresEcole || 0,
              nbHeuresFormation: heurePointeeDto?.nbHeuresFormation || 0,
              nbHeuresChantier: heurePointeeDto?.nbHeuresChantier || 0,
              nbHeuresTotales: heurePointeeDto.nbHeuresTotales || 0,
              projection: heurePointeeDto.projection || '',
              engagement: heurePointeeDto.engagement || 0,
              commentaire: heurePointeeDto.commentaire || '',
              periode: heurePointeeChantier.periode || '',
              isCompagnonETT,
              nbPieceJointe: heurePointeeDto.nbPieceJointe || 0,
            }
          })
          .sort((a, b) => {
            if (a.isCompagnonETT !== b.isCompagnonETT) {
              return Number(a.isCompagnonETT) - Number(b.isCompagnonETT)
            }
            return a.id - b.id
          })
      })
    )
  }

  private showDeleteModal(
    insertionChantier: InsertionChantierDto,
    heurePointeeChantier: HeurePointeeChantierDto,
    insertionId: number
  ): Observable<string> {
    const totalHeuresInsertionChantierToDelete =
      heurePointeeChantier?.heures?.find((heure) => heure.id === insertionId)?.nbHeuresTotales || 0

    if (totalHeuresInsertionChantierToDelete === 0) {
      return of('confirm')
    }

    const modalRef = this.modalService.open(ConfirmationModalComponent)
    const modalInfos: Record<InsertionChantierType, string> = {
      [InsertionChantierType.SOUS_TRAITANT]: 'Suppression sous-traitant',
      [InsertionChantierType.AUTRE_SALARIE_LG]: 'Suppression salarié LG',
      [InsertionChantierType.AUTRE_CAS]: 'Suppression autre cas',
      [InsertionChantierType.COMPAGNON_LG]: 'NON SUPPRIMABLE - CE TEXTE NE DOIT PAS ETRE AFFICHE',
      [InsertionChantierType.COMPAGNON_ETT]: 'NON SUPPRIMABLE - CE TEXTE NE DOIT PAS ETRE AFFICHE',
      [InsertionChantierType.COMPAGNON_SOUS_TRAITANT]: 'Suppression compagnon sous-traitant',
    }
    modalRef.componentInstance.modalTitle = modalInfos[insertionChantier.type]
    modalRef.componentInstance.confirmationText = `"${insertionChantier.nom}" possède ${totalHeuresInsertionChantierToDelete} h d'insertion. Si vous le supprimez, ses heures ne seront plus comptabilisées !\n\nÊtes-vous sûr de vouloir le supprimer ?`
    modalRef.componentInstance.confirmButtonLabel = 'OK'
    modalRef.componentInstance.cancelButtonLabel = 'Annuler'

    return modalRef.closed
  }
}
